import { useState, useRef, useEffect } from "react";
import { TAKEOFF_REPORTS, buildTakeoffCsv } from "../../utils/takeoffExport";
import { useUnitsPreference } from "../../utils/useUnits";

// "Export takeoff" — the revision's takeoff quantities as CSV, by room, by drawing
// or by condition. Built from the rows already loaded on the page; nothing is
// sent anywhere. The file starts with a UTF-8 byte-order mark so Excel reads
// accented text correctly.

function downloadCsv(filename, csv) {
  const blob = new Blob(["﻿", csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function TakeoffExportMenu({ rooms, conditions, sheets, project, revision }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [units] = useUnitsPreference(); // the CSV follows the display units (metric: m, m²)
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  function run(id) {
    try {
      const out = buildTakeoffCsv(id, { rooms, conditions, sheets }, { project, revision, units });
      downloadCsv(out.filename, out.csv);
      setNote(`${out.rowCount} row${out.rowCount === 1 ? "" : "s"} → ${out.filename}`);
    } catch (e) {
      setNote("Export failed: " + e.message);
    }
    setOpen(false);
  }

  const empty = !rooms.length && !conditions.length;
  return (
    <div ref={ref} style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 8 }}>
      <button
        type="button" onClick={() => setOpen(o => !o)} disabled={empty}
        title={empty ? "Nothing to export yet — draw or place some takeoff first" : "Download the takeoff quantities as CSV (opens in Excel)"}
        style={{ height: 30, padding: "0 12px", whiteSpace: "nowrap", fontSize: 12, fontWeight: 700, cursor: empty ? "default" : "pointer", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", background: "var(--surface)", color: "var(--text2)" }}
      >
        ⬇ Export takeoff (CSV) ▾
      </button>
      {open && (
        <div role="menu" style={{ position: "absolute", top: 34, right: 0, zIndex: 30, minWidth: 250, background: "var(--surface)", border: "1px solid var(--cs-border)", borderRadius: 8, boxShadow: "0 6px 20px rgba(0,0,0,0.15)", padding: 4 }}>
          {TAKEOFF_REPORTS.map(r => (
            <button key={r.id} type="button" role="menuitem" onClick={() => run(r.id)}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "7px 10px", fontSize: 12.5, background: "transparent", border: "none", cursor: "pointer", color: "var(--text)" }}>
              <strong>{r.label}</strong>
              <div style={{ fontSize: 11, color: "var(--text3)" }}>
                {r.id === "room" ? "One row per room × condition, with room dimensions and floor area" : r.id === "drawing" ? "One row per drawing × condition" : "Totals per condition and where it was taken off"}
              </div>
            </button>
          ))}
        </div>
      )}
      {note && <span style={{ fontSize: 11, color: "var(--text3)" }}>{note}</span>}
    </div>
  );
}
